import {
  FormLabel,
  FormControl,
  Stack,
  Typography,
  TextField,
  Divider,
  Box,
} from '@mui/material';

import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { NumericBulletSection } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/numericBulletSection';
import type { BackupVault } from '@/data/vaults/backupVault';

import { KmsCopyablePolicy } from './kmsCopyablePolicy';
import { KmsEditKeyPolicyButton } from './kmsEditKeyPolicyButton';

import { BottomDialog } from '../dialogs/bottomDialog';
import { PanelWrapper } from '../panels/panelWrapper';
import { ReadMoreLink } from '../shared/readMoreLink';

const KmsKeyDescription = () => {
  return (
    <Typography variant='body1' component={'span'}>
      Set the KMS key to encrypt snapshots with. You’re responsible for securing
      the key from unauthorized access and deletion. Without this key, snapshots
      can’t be retrieved from the vault.{' '}
      <ReadMoreLink href='/docs/kb/feature-previews/customer-managed-vault-keys' />
    </Typography>
  );
};

export const KMSKeyEditorPanel = ({
  open,
  onClose,
  disabled,
  data,
  setData,
}: {
  open: boolean;
  onClose: () => void;
  disabled: boolean;
  data: BackupVault;
  setData: (data: BackupVault) => void;
}) => {
  return (
    <BottomDialog open={open} isFullScreen={true}>
      <PanelWrapper
        header={{
          title: 'Set KMS Key',
          subtitle: <KmsKeyDescription />,
          fullWidthSubtitle: false,
          onClose: onClose,
        }}
        footer={{
          primaryButton: {
            text: 'Done',
            'data-testid': 'done-button',
            onClick: onClose,
          },
          secondaryButton: {
            text: 'Cancel',
            'data-testid': 'cancel-button',
            onClick: onClose,
          },
        }}
      >
        <Box padding='40px min(100px,6vw)' width='100%' height='auto'>
          <Stack
            gap='40px'
            divider={<Divider sx={{ borderStyle: 'dashed' }} />}
          >
            <NumericBulletSection bulletNumber={1} title='KMS key ARN'>
              <BulletItem
                leftContent={
                  <FormControl size='small' className='w-full px-[0px]'>
                    <FormLabel>{'KMS key ARN'}</FormLabel>
                    <TextField
                      disabled={disabled}
                      size='small'
                      defaultValue={data.vaultAttributes.aws?.encryptionKey}
                      inputProps={{
                        'data-testid': 'vault-encryption-key',
                      }}
                      onChange={(event) => {
                        setData({
                          ...data,
                          vaultAttributes: {
                            ...data.vaultAttributes,
                            aws: {
                              ...data.vaultAttributes.aws,
                              encryptionKey: event.target.value,
                            },
                          },
                        });
                      }}
                      placeholder='Example: arn:aws:kms:us-east-1:123456789012:key/f6e3953c-1ee8-5da5-b9a4-66ca5095ffda'
                    />
                  </FormControl>
                }
              />
            </NumericBulletSection>
            <NumericBulletSection bulletNumber={2} title='Add policy in AWS'>
              <BulletItem
                title='Review and add this policy in your AWS account. Afterward, come back to this tab to finish the connection.'
                leftContent={<KmsEditKeyPolicyButton backupVault={data} />}
                rightContent={<KmsCopyablePolicy backupVault={data} />}
              />
            </NumericBulletSection>
          </Stack>
        </Box>
      </PanelWrapper>
    </BottomDialog>
  );
};
